import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createAgencyClient } from './api';
import type { AgencyFilter } from './model';

export const agenciesQueryKey = ['supply', 'agencies'] as const;
export function useAgencyClient(networkId?: string) {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createAgencyClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: networkId ?? config.networkId },
    accessToken: session.accessToken,
  });
}
export function useAgenciesQuery(filter: AgencyFilter) {
  const config = readShigoAppConfig();
  const client = useAgencyClient();
  return useQuery({
    queryKey: [...agenciesQueryKey, config.networkId, 'list', filter],
    queryFn: () => client.list(filter),
  });
}
export function useAgencyQuery(id: string) {
  const { networkId } = readShigoAppConfig();
  const client = useAgencyClient();
  return useQuery({
    queryKey: [...agenciesQueryKey, networkId, 'detail', id],
    queryFn: () => client.get(id),
    enabled: Boolean(id),
  });
}
export function useAgencyMutations() {
  const { networkId } = readShigoAppConfig();
  const client = useAgencyClient();
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: [...agenciesQueryKey, networkId, 'list'] });
    void queryClient.invalidateQueries({ queryKey: [...agenciesQueryKey, networkId, 'options'] });
  };
  return {
    create: useMutation({ mutationFn: client.create, onSuccess: invalidate }),
    update: useMutation({
      mutationFn: ({ id, name }: { id: string; name: string }) => client.update(id, { name }),
      onSuccess: (agency) => {
        queryClient.setQueryData([...agenciesQueryKey, networkId, 'detail', agency.id], agency);
        invalidate();
        // Renaming an Agency also changes the ownership label on Brands.
        void queryClient.invalidateQueries({
          queryKey: [...agenciesQueryKey, networkId, 'brand-assignments'],
        });
      },
    }),
  };
}

export function useAgencyOptions(networkId: string, enabled: boolean) {
  const client = useAgencyClient(networkId);
  return useQuery({
    queryKey: [...agenciesQueryKey, networkId, 'options'],
    enabled: enabled && Boolean(networkId),
    queryFn: async () => {
      const agencies = [];
      let page = 0;
      let total = 0;
      do {
        const result = await client.list({ name: '', page, size: 100 });
        agencies.push(...result.content);
        total = result.totalElements;
        if (!result.content.length) break;
        page += 1;
      } while (agencies.length < total);
      return agencies;
    },
  });
}

export function useAgencyBrandAssignments(enabled: boolean, agencyId?: string) {
  const { networkId } = readShigoAppConfig();
  const client = useAgencyClient();
  return useQuery({
    queryKey: [
      ...agenciesQueryKey,
      networkId,
      'brand-assignments',
      ...(agencyId ? [agencyId] : []),
    ],
    enabled,
    queryFn: async () => {
      const content = [];
      let page = 0;
      let total = 0;
      do {
        const result = await client.listBrands(page, agencyId);
        content.push(...result.content);
        total = result.totalElements;
        if (!result.content.length) break;
        page += 1;
      } while (content.length < total);
      return content;
    },
  });
}
